import DraggableModal from '../../generic/DraggableModal'
import { useEffect, useState } from 'react'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import Typography from '@mui/material/Typography'
import { toPrice } from '../../../helpers/stringFormat'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import TermsAndConditionsCopy from './TermsAndConditionsCopy'
import PermissionGate from '../../../helpers/PermissionGate'
import GenerateCodeV2 from '../../formItems/GenerateCodeV2'
import StyledButton from '../../generic/StyledButton'
import Tooltip from '@mui/material/Tooltip'
import { useUcs } from '../../../helpers/contexts/UserCompanySettingsContext'
import { errorMessage } from '../../../helpers/errorMessageFunc'
import { backendPatchWithoutRefresh } from '../../../helpers/callBackend'
import { message } from 'antd'

const BulkApprovalsModal = ({ modalVisible, handleCancelModal, selectedRowObjects, updateBulkPaymentRequests }) => {
  const [displayData, setDisplayData] = useState([])
  const [feesRequired, setFeesRequired] = useState(false)
  const [feesApproved, setFeesApproved] = useState(false)
  const [tAndCApproved, setTAndCApproved] = useState(false)
  const [otp, setOtp] = useState('')
  const [userCanApprove, setUserCanApprove] = useState(true)
  const [userLimitMessage, setUserLimitMessage] = useState('')

  const ucsContext = useUcs()

  const handleApprove = async () => {
    const selectedIds = selectedRowObjects.map((paymentRequest) => {
      return paymentRequest.id
    })

    if (!tAndCApproved) {
      errorMessage('Please approve our Terms and Conditions')
      return
    }

    if (feesRequired && !feesApproved) {
      errorMessage('Please approve associated fees')
      return
    }

    if (!otp) {
      errorMessage('Please input the 6-digit verification code sent to your phone')
      return
    }

    const approvalResp = await backendPatchWithoutRefresh('/api/payments/payment-request/multi/', {
      add_approver: true,
      otp: otp,
      payment_request_ids: selectedIds,
    })

    approvalResp.data?.data?.failure_reasons?.forEach((failureReason) => {
      errorMessage(failureReason, 0)
    })

    message.success(`Approved ${approvalResp.data?.data?.approved_prs?.length || 0} payment request(s)`)
    message.success(`Initiated ${approvalResp.data?.data?.initiated_transactions?.length || 0} transaction(s)`)
    updateBulkPaymentRequests()
    handleCancelModal(false)
  }

  // determine if user can approve all payment requests based on limits
  useEffect(() => {
    if (!feesApproved || !tAndCApproved) {
      return
    }

    if (!ucsContext.loaded) {
      errorMessage(
        'Sorry, we could not load your user permissions before attempting to approve multiple payment requests'
      )
    }

    selectedRowObjects.every((paymentRequest) => {
      let limit = 'approve'
      if (paymentRequest.created_by == ucsContext.userName()) {
        limit = 'self-approve'
        setUserLimitMessage(
          `$${paymentRequest.amount} exceeds your self-approval limit of $${ucsContext.selfApproveLimit()}`
        )
      } else {
        setUserLimitMessage(`$${paymentRequest.amount} exceeds your approval limit of $${ucsContext.approveLimit()}`)
      }
      if (!ucsContext.checkLimit(limit, paymentRequest.amount)) {
        setUserCanApprove(false)
        return false
      }
      return true
    })
  }, [feesApproved, tAndCApproved])

  useEffect(() => {
    setDisplayData([])

    let feeSum = 0
    const newDisplayData = selectedRowObjects.map((paymentRequest) => {
      let fees = (
        <Typography fontStyle='italic' color='secondary'>
          None
        </Typography>
      )

      if (paymentRequest.fees.length > 0) {
        fees = paymentRequest.fees.map((fee) => {
          feeSum += fee.amount
          return (
            <Typography>
              {fee.name}: {toPrice(fee.amount)}
            </Typography>
          )
        })
      }

      return (
        <TableRow>
          <TableCell>
            <Typography>{paymentRequest.description}</Typography>
          </TableCell>
          <TableCell>
            <Typography>{paymentRequest.sender_name}</Typography>
          </TableCell>
          <TableCell>
            <Typography>{paymentRequest.counterparty_name}</Typography>
          </TableCell>
          <TableCell>
            <Typography>{toPrice(paymentRequest.amount)}</Typography>
          </TableCell>
          <TableCell>{fees}</TableCell>
          <TableCell>
            <Typography>{toPrice(parseFloat(paymentRequest.amount) + feeSum)}</Typography>
          </TableCell>
        </TableRow>
      )
    })
    if (feeSum > 0) {
      setFeesRequired(true)
    } else {
      // if fees not needed, set to approved
      setFeesApproved(true)
    }
    setDisplayData(newDisplayData)
  }, [selectedRowObjects])

  return (
    <DraggableModal
      width='auto'
      modalVisible={modalVisible}
      handleCancelModal={handleCancelModal}
      modalTitle='Approve Payment Requests'
      content={
        <Stack spacing={1}>
          <TableContainer>
            <Table>
              <TableHead sx={{ fontStyle: 'italic' }}>
                <TableRow>
                  <TableCell>
                    <Typography>Description</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>From</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>To</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Amount</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Fees</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Total</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {displayData}
            </Table>
          </TableContainer>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox defaultChecked={tAndCApproved} onChange={(event) => setTAndCApproved(event.target.checked)} />
              }
              label={<TermsAndConditionsCopy />}
            />
            {feesRequired && (
              <FormControlLabel
                control={
                  <Checkbox defaultChecked={feesApproved} onChange={(event) => setFeesApproved(event.target.checked)} />
                }
                label='By checking this box you acknowledge the above facillitator fee will be applied to this payment.'
              />
            )}
          </FormGroup>
          <GenerateCodeV2 setOtp={setOtp} />
        </Stack>
      }
      actions={
        <Stack direction='row' spacing={3} width='100%'>
          <PermissionGate
            condition={userCanApprove}
            readOnly={
              <Tooltip placement='top' arrow title={<Typography>{userLimitMessage}</Typography>}>
                <span style={{ width: '100%' }}>
                  <StyledButton label='Yes' fontSize={14} disabled={true} customTheme='outlined' />
                </span>
              </Tooltip>
            }
          >
            {feesApproved && tAndCApproved ? (
              <StyledButton label='Yes' fontSize={14} onClick={handleApprove} />
            ) : (
              <StyledButton label='Yes' fontSize={14} disabled={true} customTheme='outlined' />
            )}
          </PermissionGate>
          <StyledButton
            label='No'
            customTheme='outlined'
            fontSize={14}
            onClick={() => {
              setTAndCApproved(false)
              setFeesApproved(false)
              handleCancelModal(false)
            }}
          />
        </Stack>
      }
    />
  )
}

export default BulkApprovalsModal
